import axios, { AxiosError } from "axios";
import { useCallback, useContext } from "react";
import useSWR from "swr";
import { AuthContext } from "../../../context/AuthContext";
import { AdminEndpointEnum } from "../../../enums/endpoints";
import { ADMIN_API_URL } from "../../../utils/env";
import { ToastEnum, ToastStatusEnum, useToast } from "../../toast";

export interface ICryptoMajorsInstruments {
  instruments: string[];
}

// useCryptoMajorsInstruments wraps GET/POST /crypto-majors-instruments.
// The backend rejects empty lists, duplicates, and entries that don't match
// a registered perpetual, so we surface its error message verbatim. Status
// is polled every 30s so the UI reflects updates from other admins.
export const useCryptoMajorsInstruments = () => {
  const { queryConfig, tokenId } = useContext(AuthContext);
  const { addToast } = useToast();

  const endpoint = ADMIN_API_URL(AdminEndpointEnum.CRYPTO_MAJORS_INSTRUMENTS);
  const swr = useSWR<ICryptoMajorsInstruments, AxiosError>(
    tokenId ? [endpoint, tokenId] : undefined,
    async () => {
      const response = await axios.get<Partial<ICryptoMajorsInstruments>>(
        endpoint,
        queryConfig()
      );
      const raw = response.data || {};
      return {
        instruments: raw.instruments ?? [],
      };
    },
    { refreshInterval: 30000 }
  );

  const withAuthHeaders = useCallback(
    () => ({
      headers: {
        Authorization: `Bearer ${tokenId}`,
      },
    }),
    [tokenId]
  );

  const updateCryptoMajorsInstruments = useCallback(
    async (instruments: string[]) => {
      try {
        await axios.post(
          endpoint,
          {
            crypto_majors_instruments: instruments,
          },
          withAuthHeaders()
        );
        addToast(
          {
            type: ToastEnum.SIMPLE,
            header: "Crypto majors instruments updated",
            subheader: `${instruments.length} symbol${
              instruments.length === 1 ? "" : "s"
            }: ${instruments.join(", ")}`,
            status: ToastStatusEnum.SUCCESS,
          },
          4000
        );
        swr.mutate();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
        const payload = (error as AxiosError)?.response?.data as any;
        const subheader = payload?.error || payload?.message || "Unknown error";
        addToast(
          {
            type: ToastEnum.SIMPLE,
            header: "Update failed",
            subheader,
            status: ToastStatusEnum.ERROR,
          },
          5000
        );
      }
    },
    [addToast, endpoint, swr, withAuthHeaders]
  );

  return {
    ...swr,
    updateCryptoMajorsInstruments,
  };
};
